import * as React from 'react'
import { supabase } from './supabase'
import { AdminField, Button } from './ui'
import { productGroupLabel } from './ProductSummary'
import { parseDeliveryFeeGbp } from './deliveryAreaCsv'

type Tariff = { id: string; default_fee_pence: number; free_outward_codes: string[] }
type ProductTariff = { product_group: string; delivery_tariff: Tariff | null }

const message = (error: unknown) => error && typeof error === 'object' && 'message' in error
  ? String(error.message) === 'TARIFF_CHANGED_REFRESH'
    ? 'This tariff has changed. Reload the page before saving again.'
    : String(error.message)
  : 'The delivery tariff could not be loaded or saved.'

export function parseFreeOutwardCodes(value: string): string[] {
  const codes = [...new Set(value.toUpperCase().split(/[\s,;]+/).filter(Boolean))].sort()
  const invalid = codes.filter(code => !/^([A-Z]{1,2}[0-9][0-9A-Z]?|GIR)$/.test(code))
  if (invalid.length) throw new Error(`Enter outward codes only, such as GU1 or SW1A. Check: ${invalid.slice(0, 5).join(', ')}.`)
  if (codes.length > 2000) throw new Error('Use no more than 2,000 outward codes.')
  return codes
}

export function ProductDeliveryTariffs({ canWrite }: { canWrite: boolean }) {
  const [types, setTypes] = React.useState<ProductTariff[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState('')

  React.useEffect(() => {
    let live = true
    async function load() {
      try {
        const { data, error } = await supabase.from('product_types')
          .select('product_group, delivery_tariff:product_delivery_tariff_versions!product_types_delivery_tariff_fkey(id,default_fee_pence,free_outward_codes)')
          .order('product_group')
        if (error) throw error
        if (live) setTypes((data ?? []) as unknown as ProductTariff[])
      } catch (error) {
        if (live) setError(message(error))
      } finally {
        if (live) setLoading(false)
      }
    }
    void load()
    return () => { live = false }
  }, [])

  return <section className="panel settings-editors delivery-tariffs">
    <div className="panel-heading"><h2>Delivery tariffs by hire type</h2></div>
    <p>Each tariff covers delivery and collection, charged once per booking. Changes apply to new quotes; existing payment sessions keep their tariff.</p>
    {loading && <p>Loading delivery tariffs…</p>}
    {error && <p className="message error" role="alert">{error}</p>}
    {types.filter(type => type.delivery_tariff).map(type => <TariffEditor
      key={type.product_group} group={type.product_group} tariff={type.delivery_tariff!} canWrite={canWrite} />)}
  </section>
}

function TariffEditor({ group, tariff, canWrite }: { group: string; tariff: Tariff; canWrite: boolean }) {
  const [version, setVersion] = React.useState(tariff.id)
  const [fee, setFee] = React.useState((tariff.default_fee_pence / 100).toFixed(2))
  const [codes, setCodes] = React.useState(tariff.free_outward_codes.join(', '))
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState('')
  const [saved, setSaved] = React.useState(false)

  const save = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!canWrite || busy) return
    setError(''); setSaved(false)
    try {
      const amount = parseDeliveryFeeGbp(fee)
      if (amount == null) throw new Error('Enter a delivery charge of £0 or more, with at most two decimal places.')
      const freeCodes = parseFreeOutwardCodes(codes)
      setBusy(true)
      const { data, error } = await supabase.rpc('admin_save_product_delivery_tariff', {
        p_product_group: group,
        p_default_fee_pence: amount,
        p_free_outward_codes: freeCodes,
        p_expected_tariff_id: version,
      })
      if (error) throw error
      setVersion(data as string)
      setCodes(freeCodes.join(', '))
      setFee((amount / 100).toFixed(2))
      setSaved(true)
    } catch (error) {
      setError(message(error))
    } finally {
      setBusy(false)
    }
  }

  return <form className="settings-editor" onSubmit={event => void save(event)}>
    <h3>{productGroupLabel(group)}</h3>
    <fieldset disabled={!canWrite || busy}>
      <AdminField>Charge outside free postcodes (£)<input inputMode="decimal" value={fee} required
        onChange={event => { setFee(event.target.value); setSaved(false) }} /></AdminField>
      <AdminField>Free outward postcodes<textarea rows={7} value={codes}
        onChange={event => { setCodes(event.target.value); setSaved(false) }} /></AdminField>
      <p>Separate codes with commas or new lines. Exact district matching applies: GU1 and GU10 are separate codes. An empty list charges the default everywhere.</p>
      {canWrite && <Button className="primary">{busy ? 'Saving…' : 'Save delivery tariff'}</Button>}
    </fieldset>
    {error && <p className="message error" role="alert">{error}</p>}
    {saved && <p role="status">Delivery tariff saved.</p>}
  </form>
}
